import { useEffect, useRef, type MutableRefObject } from 'react';

import { dispatchBizyChannelEvent, type BizyChannelEvent, type BizyChannelOptions } from '../lib/bizy/channel';
import { useConsumer } from '../lib/channels/hooks/use-consumer';

// Re-export the event types so consumers of this hook
// (`use-bizy-streaming-chat`, `use-bizy-rerun-chat`, tests) can keep
// their existing import paths.
export type {
	BizyActionEvent,
	BizyCancelledEvent,
	BizyChannelEvent,
	BizyChannelOptions,
	BizyCompleteEvent,
	BizyErrorEvent,
	BizyNodeAppendedEvent,
	BizyNodeTokenEvent,
	BizyPreliminaryTokenEvent,
	BizyProactiveConversationStartedEvent,
	BizySupportEscalatedEvent,
	BizySupportHandoffEvent,
	BizySupportResolvedEvent,
	BizyToolCallEvent,
} from '../lib/bizy/channel';

/**
 * ActionCable-specific options: everything the pure dispatcher accepts, plus
 * a React ref for the active request id (kept as a ref, not a plain value, so
 * the WebSocket handler always reads the latest id synchronously — React state
 * batching would otherwise let a stale value route legitimate events to the
 * `onOtherTab*` branch).
 */
export interface UseBizyChannelOptions extends BizyChannelOptions {
	activeRequestIdRef?: MutableRefObject<string | undefined>;
}

/**
 * Hook for subscribing to BizyChannel via ActionCable.
 *
 * Owns the transport (consumer subscription lifecycle, connected/disconnected
 * callbacks). Event routing — matching `activeRequestIdRef.current` against
 * each event's `request_id` and dispatching to the right callback — is
 * delegated to the shared `dispatchBizyChannelEvent` from `@bonusly/bizy-kit`
 * so the sidebar's `useBizyChatState` and this hook stay in lockstep on cross-
 * tab isolation semantics.
 */
export function useBizyChannel(userId: string | undefined, options: UseBizyChannelOptions) {
	const consumer = useConsumer();
	const subscriptionRef = useRef<any>(null);

	const optionsRef = useRef(options);
	optionsRef.current = options;

	useEffect(() => {
		if (!consumer || !userId) return;

		const timer = setTimeout(() => {
			const subscription = consumer.subscriptions.create(
				{ channel: 'BizyChannel' },
				{
					connected: () => {
						optionsRef.current.onConnected?.();
					},
					disconnected: () => {
						optionsRef.current.onDisconnected?.();
					},
					rejected: () => {
						optionsRef.current.onDisconnected?.();
					},
					received: (message: BizyChannelEvent) => {
						dispatchBizyChannelEvent(message, optionsRef.current, optionsRef.current.activeRequestIdRef?.current);
					},
				}
			);

			subscriptionRef.current = subscription;
		}, 0);

		return () => {
			clearTimeout(timer);
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
		};
	}, [consumer, userId]);
}
